import { useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { ClipboardCheck, ChevronDown } from 'lucide-react';

const STATUS_META = {
  aangevraagd: { label: 'Keuring aangevraagd', cls: 'bg-blue-100 text-blue-700 border-blue-300' },
  ingepland: { label: 'Keuring ingepland', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  goedgekeurd: { label: 'Keuring goedgekeurd', cls: 'bg-green-100 text-green-700 border-green-300' },
  afgekeurd: { label: 'Keuring afgekeurd', cls: 'bg-red-100 text-red-700 border-red-300' },
};

export const KeuringStatusControl = ({ installation, onUpdated, endpoint, size = 'sm' }) => {
  const putUrl = endpoint || `/scheduled-installations/${installation.id}/keuring`;
  const kr = installation.keuring_request;
  const status = kr?.status && kr.status !== 'niet_aangevraagd' ? kr.status : null;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const update = async (payload, successMsg) => {
    setSaving(true);
    try {
      const res = await api.put(putUrl, payload);
      onUpdated(res.data.keuring_request);
      toast.success(successMsg);
      setDialogOpen(false);
    } catch (e) {
      toast.error('Kon keuringstatus niet bijwerken');
    } finally {
      setSaving(false);
    }
  };

  if (!status) {
    return (
      <>
        <Button variant="outline" size="sm" className={size === 'sm' ? 'h-6 px-2 text-[11px]' : ''}
          onClick={() => { setReference(''); setNotes(''); setDialogOpen(true); }}
          data-testid={`keuring-aanvragen-${installation.id}`}>
          <ClipboardCheck className={size === 'sm' ? 'h-3 w-3 mr-1' : 'w-4 h-4 mr-1.5'} /> Keuring aanvragen
        </Button>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md" data-testid="keuring-dialog">
            <DialogHeader><DialogTitle>Keuring aanvragen</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">Registreer de keuringsaanvraag (SPOQ). Zodra de SPOQ API-koppeling actief is, wordt de aanvraag automatisch doorgestuurd.</p>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">SPOQ-referentie / dossiernummer (optioneel)</label>
                <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="bv. SPOQ-2026-12345" data-testid="keuring-reference-input" />
              </div>
              <div>
                <label className="text-sm font-medium">Notities (optioneel)</label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="bv. keurder gecontacteerd op ..." data-testid="keuring-notes-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuleren</Button>
              <Button disabled={saving} onClick={() => update({ status: 'aangevraagd', spoq_reference: reference, notes }, 'Keuring geregistreerd als aangevraagd')} data-testid="keuring-submit-btn">
                Aanvraag registreren
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  const meta = STATUS_META[status];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={`inline-flex items-center gap-1 rounded-full border font-medium ${meta.cls} ${size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-3 py-1.5 text-sm'}`}
          data-testid={`keuring-status-${installation.id}`}>
          <ClipboardCheck className="h-3 w-3" /> {meta.label}
          {kr?.spoq_reference ? ` · ${kr.spoq_reference}` : ''}
          <ChevronDown className="h-3 w-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {Object.entries(STATUS_META).map(([key, m]) => (
          <DropdownMenuItem key={key} disabled={key === status}
            onClick={() => update({ status: key }, `Status: ${m.label}`)} data-testid={`keuring-set-${key}`}>
            {m.label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem className="text-destructive" onClick={() => update({ status: 'niet_aangevraagd' }, 'Keuringsaanvraag gereset')} data-testid="keuring-reset">
          Reset (niet aangevraagd)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
